import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { fetchShippingToday, fetchOrderProfit, computePackingCost, fetchProfitPerBoxSummary } from '../services/api';
import { toTitleCase } from '../utils/format';
import VipBadge from '../components/VipBadge';

// Margin buckets (share of revenue kept as profit). Adjust here if needed.
const GOOD_PCT = 20;   // green  — healthy margin
const POOR_PCT = 10;   // red    — thin/negative; below this is "poor"

const money = (v) => `${v < 0 ? '-' : ''}$${Math.abs(Number(v || 0)).toFixed(2)}`;
const num = (v) => Number(String(v).replace(/[$,]/g, '')) || 0;

function bucketOf(marginPct) {
  if (marginPct >= GOOD_PCT) return 'good';
  if (marginPct < POOR_PCT) return 'poor';
  return 'neutral';
}
const BUCKET_COLOR = { good: '#1e7e34', neutral: '#b8860b', poor: '#c0392b' };
const BUCKET_BG = { good: 'rgba(30,126,52,0.10)', neutral: 'rgba(184,134,11,0.12)', poor: 'rgba(192,57,42,0.10)' };

function ExpandDetail({ internalId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchOrderProfit(internalId).then(x => { if (alive) setD(x); }).catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [internalId]);

  if (err) return <div style={{ padding: 12, fontSize: 12, color: '#c0392b' }}>Couldn't load detail: {err}</div>;
  if (!d) return <div style={{ padding: 12, fontSize: 12, color: 'var(--gray-500)' }}>Loading box detail…</div>;

  const pb = d.packConfig ? computePackingCost(d.packConfig, d.effPackQty) : null;
  const packLines = pb ? [
    { label: `Box${d.effPackQty.boxType ? ` (${d.effPackQty.boxType})` : ''}`, cost: pb.box },
    ...(Number(d.effPackQty.gelPacks) ? [{ label: `Gel Packs (${Number(d.effPackQty.gelPacks)} × $${pb.gelUnit.toFixed(3)})`, cost: pb.gel }] : []),
    ...(Number(d.effPackQty.dryIce) ? [{ label: `Dry Ice (${Number(d.effPackQty.dryIce)} lb)`, cost: pb.dryice }] : []),
  ] : [{ label: 'Packing Materials', cost: d.savedPackingCost }];
  const totalCost = d.productCost + (pb ? pb.total : (d.savedPackingCost || 0)) + d.otherCost;
  const profit = d.orderAmount - totalCost;

  const row = (label, val, opts = {}) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', ...opts.style }}>
      <span style={{ color: 'var(--gray-600)' }}>{label}</span><span style={{ fontWeight: opts.bold ? 700 : 400 }}>{val}</span>
    </div>
  );

  return (
    <div style={{ padding: '12px 16px', background: 'var(--gray-50)', fontSize: 12, display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
      <div>
        <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Items</div>
        {d.productLines.map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid var(--gray-100)' }}>
            <span>{p.qty} × {p.name}{p.options ? <span style={{ color: 'var(--gray-500)' }}> · {p.options}</span> : null}</span>
            <span style={{ whiteSpace: 'nowrap', marginLeft: 10 }}>cost {money(p.cost)}</span>
          </div>
        ))}
      </div>
      <div>
        <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Cost breakdown</div>
        {row('Product cost', money(d.productCost))}
        {packLines.map((l, i) => row(l.label, money(l.cost)))}
        {d.otherCostLines.map((l, i) => row(l.label, money(l.cost)))}
        {row('Total cost', money(totalCost), { bold: true, style: { borderTop: '1px solid var(--gray-200)', marginTop: 4, paddingTop: 6 } })}
        {row('Revenue', money(d.orderAmount))}
        {row('Profit', <span style={{ color: profit >= 0 ? BUCKET_COLOR.good : BUCKET_COLOR.poor }}>{money(profit)}</span>, { bold: true })}
      </div>
    </div>
  );
}

export default function ProfitPerBox({ navigateTo }) {
  const [date, setDate] = useState(new Date());
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('profit'); // profit | margin | id | name
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('all'); // all | good | neutral | poor
  const [expanded, setExpanded] = useState(() => new Set());
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const fmtDate = (dt) => `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
  const shiftDate = (days) => setDate(d => { const n = new Date(d); n.setDate(n.getDate() + days); return n; });
  const dateLabel = fmtDate(date);

  useEffect(() => {
    let alive = true;
    setRows(null); setError(null); setAi(null); setExpanded(new Set());
    fetchShippingToday(dateLabel)
      .then(res => {
        if (!alive) return;
        const enriched = res.orders.map(o => {
          const revenue = num(o.total);
          const marginPct = revenue ? (o.profit / revenue) * 100 : 0;
          return { ...o, revenue, marginPct, bucket: bucketOf(marginPct) };
        });
        setRows(enriched);
      })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [dateLabel]);

  const stats = useMemo(() => {
    if (!rows) return null;
    const count = rows.length;
    const totalRevenue = rows.reduce((s, o) => s + o.revenue, 0);
    const totalProfit = rows.reduce((s, o) => s + o.profit, 0);
    const good = rows.filter(o => o.bucket === 'good').length;
    const neutral = rows.filter(o => o.bucket === 'neutral').length;
    const poor = rows.filter(o => o.bucket === 'poor').length;
    const losses = rows.filter(o => o.profit < 0).length;
    const costsSaved = rows.filter(o => o.costsSaved).length;
    return {
      count, totalRevenue, totalProfit,
      avgProfit: count ? totalProfit / count : 0,
      avgMargin: totalRevenue ? (totalProfit / totalRevenue) * 100 : 0,
      good, neutral, poor, losses, costsSaved,
    };
  }, [rows]);

  // AI summary — run once rows are in.
  useEffect(() => {
    if (!rows || !stats || rows.length === 0) return;
    let alive = true;
    setAiLoading(true); setAi(null);
    const compact = rows
      .slice()
      .sort((a, b) => a.marginPct - b.marginPct)
      .slice(0, 400)
      .map(o => ({ id: o.id, name: toTitleCase(o.name), shipType: o.shipType, revenue: o.revenue.toFixed(2), profit: o.profit.toFixed(2), marginPct: o.marginPct.toFixed(1), costsSaved: o.costsSaved }));
    fetchProfitPerBoxSummary({
      label: dateLabel,
      stats: {
        boxes: stats.count, totalRevenue: stats.totalRevenue.toFixed(2), totalProfit: stats.totalProfit.toFixed(2),
        avgProfitPerBox: stats.avgProfit.toFixed(2), avgMarginPct: stats.avgMargin.toFixed(1),
        good: stats.good, neutral: stats.neutral, poor: stats.poor, losses: stats.losses,
        costsSavedShare: `${stats.costsSaved}/${stats.count}`, goodPct: GOOD_PCT, poorPct: POOR_PCT,
      },
      orders: compact,
    })
      .then(r => { if (alive) setAi(r.summary); })
      .catch(e => { if (alive) setAi('AI summary unavailable: ' + e.message); })
      .finally(() => { if (alive) setAiLoading(false); });
    return () => { alive = false; };
  }, [rows, stats, dateLabel]);

  const sorted = useMemo(() => {
    if (!rows) return [];
    const list = filter === 'all' ? rows : rows.filter(o => o.bucket === filter);
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = {
      profit: (a, b) => (a.profit - b.profit) * dir,
      margin: (a, b) => (a.marginPct - b.marginPct) * dir,
      id: (a, b) => (Number(a.id) - Number(b.id)) * dir,
      name: (a, b) => toTitleCase(a.name).localeCompare(toTitleCase(b.name)) * dir,
    }[sortField];
    return list.slice().sort(cmp);
  }, [rows, filter, sortField, sortDir]);

  const toggleExpand = (id) => setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setSort = (field) => {
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir(field === 'name' || field === 'id' ? 'asc' : 'desc'); }
  };
  const sortArrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const Card = ({ label, value, sub }) => (
    <div className="card" style={{ padding: '12px 16px', minWidth: 130 }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{sub}</div>}
    </div>
  );
  const Chip = ({ b, label, count }) => (
    <button onClick={() => setFilter(f => f === b ? 'all' : b)}
      style={{ cursor: 'pointer', border: `1.5px solid ${BUCKET_COLOR[b]}`, background: filter === b ? BUCKET_COLOR[b] : BUCKET_BG[b], color: filter === b ? '#fff' : BUCKET_COLOR[b], borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700 }}>
      {label}: {count}
    </button>
  );

  return (
    <div>
      <div className="breadcrumb">
        <a onClick={() => navigateTo('dashboard')}>Dashboard</a><span>›</span><span>Reports</span><span>›</span><span>Profit Per Box</span>
      </div>
      <div className="page-header">Profit Per Box</div>

      {/* Date nav */}
      <div className="shipping-header-bar" style={{ marginBottom: 16 }}>
        <div className="shipping-date-nav">
          <button className="nav-btn" onClick={() => shiftDate(-7)}><ChevronsLeft size={13} /></button>
          <button className="nav-btn" onClick={() => shiftDate(-1)}><ChevronLeft size={13} /></button>
          <span className="shipping-date">{dateLabel}</span>
          <button className="nav-btn" onClick={() => shiftDate(1)}><ChevronRight size={13} /></button>
          <button className="nav-btn" onClick={() => shiftDate(7)}><ChevronsRight size={13} /></button>
        </div>
        <div className="shipping-summary" style={{ fontSize: 12, color: 'var(--gray-600)' }}>
          Margin buckets: <strong style={{ color: BUCKET_COLOR.good }}>Good ≥ {GOOD_PCT}%</strong>, <strong style={{ color: BUCKET_COLOR.neutral }}>Neutral {POOR_PCT}–{GOOD_PCT}%</strong>, <strong style={{ color: BUCKET_COLOR.poor }}>Poor &lt; {POOR_PCT}%</strong>
        </div>
      </div>

      {error && <div style={{ padding: 16, fontSize: 13, color: '#c0392b', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6, marginBottom: 12 }}><strong>Error:</strong> {error}</div>}
      {!rows && !error && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading boxes…</div>}

      {rows && stats && (<>
        {/* Summary header */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <Card label="Boxes" value={stats.count} sub={`${stats.costsSaved} costs saved`} />
          <Card label="Total Profit" value={money(stats.totalProfit)} sub={`on ${money(stats.totalRevenue)} revenue`} />
          <Card label="Avg / Box" value={money(stats.avgProfit)} />
          <Card label="Avg Margin" value={`${stats.avgMargin.toFixed(1)}%`} />
          <Card label="Losses" value={stats.losses} sub="boxes under $0" />
        </div>

        {/* Bucket chips */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip b="good" label="Good" count={stats.good} />
          <Chip b="neutral" label="Neutral" count={stats.neutral} />
          <Chip b="poor" label="Poor" count={stats.poor} />
          {filter !== 'all' && <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setFilter('all')}>Clear filter</button>}
        </div>

        {/* AI summary */}
        <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--orange)' }}>
          <div className="card-body" style={{ display: 'flex', gap: 12 }}>
            <Sparkles size={18} style={{ color: 'var(--orange)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--gray-700)' }}>
              {aiLoading ? <span style={{ color: 'var(--gray-500)' }}>Reading the day’s boxes…</span> : (ai || 'No summary.')}
            </div>
          </div>
        </div>

        {/* Sort controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 12, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Sort:</span>
          {[['profit', 'Profit'], ['margin', 'Margin'], ['id', 'Order ID'], ['name', 'Name']].map(([f, lbl]) => (
            <button key={f} className={`btn ${sortField === f ? 'btn-navy' : 'btn-outline'}`} style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setSort(f)}>{lbl}{sortArrow(f)}</button>
          ))}
          <span style={{ color: 'var(--gray-500)', marginLeft: 'auto' }}>Showing {sorted.length} of {stats.count}</span>
        </div>

        {/* Table */}
        <table className="orders-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>Order ID</th>
              <th>Name (Bill To)</th>
              <th>Ship Type</th>
              <th style={{ textAlign: 'right' }}>Revenue</th>
              <th style={{ textAlign: 'right' }}>Profit</th>
              <th style={{ textAlign: 'right' }}>Margin</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(o => {
              const isOpen = expanded.has(o.internalId);
              return (
                <React.Fragment key={o.internalId}>
                  <tr style={{ background: BUCKET_BG[o.bucket], cursor: 'pointer' }} onClick={() => toggleExpand(o.internalId)}>
                    <td style={{ textAlign: 'center', color: 'var(--gray-500)' }}>{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                    <td><a className="order-link" onClick={(e) => { e.stopPropagation(); navigateTo('order-details', o.id); }}>{o.id}</a></td>
                    <td style={{ fontWeight: 600 }}>{toTitleCase(o.name)}{o.isVip && <VipBadge notRenewing={o.vipNotRenewing} style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, background: 'var(--orange)', color: '#fff', borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle' }} />}</td>
                    <td style={{ color: 'var(--gray-600)' }}>{o.shipType}</td>
                    <td style={{ textAlign: 'right' }}>{money(o.revenue)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: BUCKET_COLOR[o.bucket] }}>{o.profit >= 0 ? '+' : ''}{money(o.profit)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, color: '#fff', background: BUCKET_COLOR[o.bucket], borderRadius: 4, padding: '2px 7px', fontSize: 11 }}>{o.marginPct.toFixed(1)}%</span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan={7} style={{ padding: 0 }}><ExpandDetail internalId={o.internalId} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </>)}
    </div>
  );
}
